/* eslint-disable react/no-array-index-key */
/* eslint-disable react-hooks/exhaustive-deps */
import { SkeletonWrapper } from '@/components/SkeletonWrapper';
import {
  IconArrowDownwardMS,
  IconArrowUpwardMS,
  IconMobileDataArrowsMS,
} from '@/constants/icons';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Table as StyledTable,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { NoDataTable } from '../NoDataTable';
import { StyledTableCellBody, StyledTableCellHead } from './styles';

type Order = 'asc' | 'desc';

export interface TableHeadData<T> {
  id?: number;
  label: string;
  key: keyof T;
  sortable?: boolean;
}

interface TableProps<T> {
  data: T[] | [];
  tableHeadData: TableHeadData<T>[];
  emptyTableIcon?: ReactNode;
  emptyTableDescription?: string;
  renderRow: (row: T) => ReactNode;
  getRowId: (row: T) => string | number;
  onRowClick?: (row: T) => void;
  pageSize?: number;
  entityLabel?: string;
  records: number;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  isLoading?: boolean;
  sortableKeys?: (keyof T)[];
  threeStateSort?: boolean;
}

export function Table<T>({
  data,
  tableHeadData,
  emptyTableIcon,
  emptyTableDescription,
  renderRow,
  getRowId,
  onRowClick,
  pageSize = 10,
  entityLabel,
  records,
  page,
  setPage,
  isLoading: externalLoading,
  sortableKeys,
  threeStateSort = false,
}: TableProps<T>): JSX.Element {
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof T | null>(null);
  const [internalLoading, setInternalLoading] = useState(true);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setInternalLoading(false);
    }, 3000);

    return () => clearTimeout(timeout);
  }, []);

  const isLoading = externalLoading ?? internalLoading;

  function descendingComparator(a: T, b: T, orderBy: keyof T): number {
    const valueA = a[orderBy];
    const valueB = b[orderBy];

    const numA = Number(valueA);
    const numB = Number(valueB);

    if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
      return numB - numA;
    }

    return String(valueB ?? '').localeCompare(String(valueA ?? ''));
  }

  function getComparator(
    order: Order,
    orderBy: keyof T
  ): (a: T, b: T) => number {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  const handleSort = (property: keyof T): void => {
    if (threeStateSort && orderBy === property && order === 'desc') {
      setOrder('asc');
      setOrderBy(null);
      return;
    }

    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    if (!orderBy) return data;
    return [...data].sort(getComparator(order, orderBy));
  }, [data, order, orderBy]);

  const showPagination = records > pageSize;

  const renderTableBodyContent = (): ReactNode => {
    if (isLoading) {
      return Array.from({ length: 3 }).map((_, index) => (
        <TableRow key={index}>
          <StyledTableCellBody colSpan={tableHeadData.length}>
            <SkeletonWrapper
              isLoading={isLoading}
              variant="rectangular"
              height={64}
            />
          </StyledTableCellBody>
        </TableRow>
      ));
    }

    if (emptyTableIcon && emptyTableDescription && !data.length) {
      return (
        <TableRow>
          <StyledTableCellBody colSpan={tableHeadData.length}>
            <NoDataTable
              icon={emptyTableIcon}
              description={emptyTableDescription}
            />
          </StyledTableCellBody>
        </TableRow>
      );
    }

    return sortedData.map((row) => (
      <TableRow
        key={getRowId(row)}
        onClick={() => onRowClick?.(row)}
        className="table-row-body"
      >
        {renderRow(row)}
      </TableRow>
    ));
  };

  return (
    <TableContainer sx={{ marginTop: '16px' }}>
      <StyledTable>
        <TableHead>
          <TableRow>
            {tableHeadData.map((cell) => {
              const isActive = orderBy === cell.key;
              const isSortable =
                !sortableKeys || sortableKeys.includes(cell.key);

              let arrow = IconMobileDataArrowsMS;

              if (isActive) {
                arrow =
                  order === 'asc' ? IconArrowUpwardMS : IconArrowDownwardMS;
              }

              return (
                <StyledTableCellHead
                  key={cell.id}
                  onClick={isSortable ? () => handleSort(cell.key) : undefined}
                  sx={isSortable ? undefined : { cursor: 'default' }}
                >
                  {cell.label}
                  {isSortable && <span>{!data.length ? '' : arrow}</span>}
                </StyledTableCellHead>
              );
            })}
          </TableRow>
        </TableHead>

        <TableBody>{renderTableBodyContent()}</TableBody>
      </StyledTable>

      {showPagination && (
        <Box display="flex" justifyContent="center" alignItems="center" py={1}>
          <IconButton
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 1}
          >
            <ChevronLeft />
          </IconButton>

          <Typography variant="body2" color="text.secondary" mx={1}>
            Mostrando {Math.min(page * pageSize, records)} de {records}
            {entityLabel && ` ${entityLabel}`}
          </Typography>

          <IconButton
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= Math.ceil(records / pageSize)}
          >
            <ChevronRight />
          </IconButton>
        </Box>
      )}
    </TableContainer>
  );
}
